import { history } from 'settings'
import { store } from 'store'
import { ActionType } from 'store/auth/model'
import { storage } from 'utils'

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'

const API_BASE_URL = import.meta.env.REACT_APP_API_URL

export const instance = axios.create({
  baseURL: API_BASE_URL,
})

let _authorizing: Promise<void> | null = null

const logOut = async () => {
  storage.removeItem('accessToken')
  storage.removeItem('refreshToken')
  storage.removeItem('scanToken')

  store.dispatch({
    type: ActionType.SET_AUTH,
    payload: false,
  })

  history.push('/sign-in')
}

// @ts-ignore
const refresh = async () => {
  const refreshToken = storage.getItem('refreshToken')
  const { data, status } = await createApiCall({
    method: 'POST',
    url: '/auth/customer/refresh-tokens',
    data: {
      token: refreshToken,
    },
  })

  if (status === 201) {
    storage.setItem('accessToken', data.access_token)
    storage.setItem('refreshToken', data.refresh_token)
    storage.setItem('scanToken', data.scan_token)
  } else if (status === 401) {
    logOut()
  }

  return data
}

// @ts-ignore
const errorHandler = async (error: any) => {
  if (!error.response) {
    return JSON.parse(JSON.stringify(error))
  }

  if (error.response.status === 401) {
    const _refreshToken = storage.getItem('refreshToken')

    /*
     * VRG-720 — a 401 with NO SESSION AT ALL is not an expiry, and must not
     * redirect.
     *
     * `logOut` ends a session and sends the person to `/sign-in`. That is right
     * when a session existed and has stopped working. It is wrong when there was
     * never a session, because then a 401 is simply the expected answer to an
     * unauthenticated request — there is nothing to clear and nothing has gone
     * wrong.
     *
     * Left unguarded it broke the most public surface we have. A certificate page
     * is reached by tapping a tag, by someone who may have no account and has not
     * asked for one. That page still fires owner-only requests for every visitor
     * (`/certificates/customer/provenance-consent/:id` among them); each one 401s
     * for a signed-out visitor; and the FIRST of them replaced the certificate
     * they had just scanned with a login form. Mid-claim it is worse than
     * confusing: the claim card is gone and the item looks like it needs an
     * account to view.
     *
     * So the 401 is handed back to its caller instead, which is what a public
     * surface needs in order to quietly not render the owner-only part. A real
     * expiry — access token present, or a refresh token to try — is untouched and
     * still refreshes or logs out below.
     */
    if (!_refreshToken && !storage.getItem('accessToken')) {
      return error.response
    }

    _authorizing ??= (_refreshToken ? refresh : logOut)()
      .finally(() => (_authorizing = null))
      .catch((error: any) => Promise.reject(error))

    const originalRequestConfig = error.config
    delete originalRequestConfig.headers['X-Vera-Token']

    return _authorizing?.then(() => {
      const token = storage.getItem('accessToken')

      if (token) {
        originalRequestConfig.headers['X-Vera-Token'] = token
      }
      return axios.request(originalRequestConfig)
    })
  }

  // 403 errors indicate insufficient permissions for a specific action
  // but don't mean the user should be logged out
  // The calling code should handle the error appropriately

  // 409 errors indicate resource conflicts (e.g., duplicate connections)
  // The calling code should check error.response.data.code for specific error types
  // and display appropriate user-friendly messages

  return error.response
}

export const createApiCall = ({
  method = 'GET',
  url = '',
  data = {},
  headers = {},
  throwError = false,
  ...rest
}: AxiosRequestConfig & { throwError?: boolean }): Promise<AxiosResponse> => {
  const reqHeaders = {
    'Content-Type': 'application/json',
    'X-Vera-Token': null,
    cache: 'no-cache',
    ...headers,
  }

  const token = storage.getItem('accessToken')

  if (token) {
    // @ts-ignore
    reqHeaders['X-Vera-Token'] = token
  }

  return instance
    .request({ method, url, data, headers: reqHeaders, ...rest })
    .then(response => response)
    .catch(error => {
      if (throwError) throw error;
      return errorHandler(error);
    })
}
